/**
 * DeveloperDocs.jsx — Admin ▸ Developer tab.
 *
 * Read-only API reference for the AE Tracker Functions API. SuperAdmin-only
 * (the tab is gated in AdminPanel/ADMIN_TABS_BY_ROLE). Curated v1 — the endpoint
 * catalogue is hand-maintained; if it drifts, a GET /api/_meta/routes generator
 * is the durable fix (see api_access_logic.md). Programmatic API keys are a
 * deferred feature — see the callout at the bottom.
 */
import React from 'react';

// api://<CLIENT_ID>/user_impersonation — the delegated scope the SPA requests.
const API_CLIENT_ID = '6509e101-f34d-4fae-bebd-e6fe615bd733';
const API_SCOPE = `api://${API_CLIENT_ID}/user_impersonation`;

const METHOD_COLOR = {
  GET:    'var(--ok, #16a34a)',
  POST:   'var(--brand, #2563eb)',
  PUT:    'var(--warn, #b45309)',
  PATCH:  'var(--warn, #b45309)',
  DELETE: 'var(--danger, #dc2626)',
};

// Curated endpoint catalogue. `authz` is the dominant gate per group (every
// endpoint additionally passes token validation + geographic scope).
const ENDPOINT_GROUPS = [
  {
    name: 'Health',
    authz: 'Anonymous liveness · SuperAdmin probes',
    rows: [
      ['GET', '/api/health', 'Liveness ping (anonymous, no backend detail)'],
      ['GET', '/api/monitor/health', 'Composite health (db · blob · graph) — SuperAdmin'],
      ['GET', '/api/health/db', 'SQL connectivity probe — SuperAdmin'],
      ['GET', '/api/health/blob', 'Blob Storage probe — SuperAdmin'],
      ['GET', '/api/health/graph', 'Microsoft Graph probe — SuperAdmin'],
    ],
  },
  {
    name: 'Cases',
    authz: 'canSubmitCase / canEditCase / canDeleteCase',
    rows: [
      ['GET', '/api/cases', 'List cases (scoped to caller)'],
      ['GET', '/api/cases/{id}', 'Single case + attachments'],
      ['GET', '/api/cases/{id}/events', 'Case audit timeline'],
      ['GET', '/api/stats', 'Aggregate dashboard stats'],
      ['POST', '/api/cases', 'Create a case'],
      ['POST', '/api/cases/{id}/restore', 'Restore a soft-deleted case'],
      ['PATCH', '/api/cases/{id}', 'Update case fields / status'],
      ['DELETE', '/api/cases/{id}', 'Soft-delete a case'],
    ],
  },
  {
    name: 'Attachments',
    authz: 'canEditCase',
    rows: [
      ['POST', '/api/cases/{id}/attachments', 'Upload (multipart/form-data: file, category, metadata JSON — post photo date taken / training certificate date + trainer)'],
      ['DELETE', '/api/attachments/{id}', 'Remove an attachment'],
    ],
  },
  {
    name: 'Requirements',
    authz: 'canManageRequirements / canRespondToRequirements',
    rows: [
      ['POST', '/api/cases/{id}/requirements', 'Add a requirement'],
      ['POST', '/api/cases/{id}/requirements/{reqId}/complete', 'Mark a requirement complete'],
      ['DELETE', '/api/cases/{id}/requirements/{reqId}', 'Remove a requirement'],
      ['POST', '/api/cases/{id}/waivers', 'Record why a required document cannot be provided (canWaiveRequirements)'],
      ['DELETE', '/api/cases/{id}/waivers/{category}', 'Withdraw a waiver (canWaiveRequirements)'],
    ],
  },
  {
    name: 'Comments',
    authz: 'canComment (private needs canSeePrivateContent)',
    rows: [
      ['GET', '/api/comments?caseId={id}', 'Thread for a case'],
      ['POST', '/api/comments', 'Post a comment'],
      ['POST', '/api/comments/{id}/attachments', 'Attach a file to a comment (category, metadata as for case uploads)'],
      ['POST', '/api/comments/{id}/ack', 'Acknowledge a message (no notification; pauses a running response SLA)'],
    ],
  },
  {
    name: 'Response SLA (Code 4/5)',
    authz: 'read: case access · actions: canManageSla',
    rows: [
      ['GET', '/api/cases/{id}/sla', 'Open response leg + recent history'],
      ['POST', '/api/cases/{id}/sla/{action}', 'lift | suppress | resume (body: reason)'],
    ],
  },
  {
    name: 'Tags',
    authz: 'canAssignTags / canCreateTags',
    rows: [
      ['GET', '/api/tags', 'Tag vocabulary'],
      ['GET', '/api/tags/admin', 'Tags incl. rules (admin view)'],
      ['POST', '/api/tags', 'Create a tag'],
      ['POST', '/api/cases/{id}/tags', 'Assign tag(s) to a case'],
      ['POST', '/api/tags/rules/preview', 'Preview auto-tag rule matches'],
      ['POST', '/api/tags/rules/backfill', 'Apply a rule to existing cases'],
      ['PUT', '/api/tags/{id}', 'Update a tag'],
      ['PUT', '/api/tags/{id}/rule', 'Update a tag auto-rule'],
      ['DELETE', '/api/tags/{id}', 'Delete a tag'],
      ['DELETE', '/api/cases/{id}/tags/{tagId}', 'Unassign a tag'],
    ],
  },
  {
    name: 'Costs',
    authz: 'canSeeCosts / canEditCosts',
    rows: [
      ['GET', '/api/cases/{caseId}/costs', 'Costs for a case'],
      ['GET', '/api/costs/by-case', 'Cost roll-up by case'],
      ['POST', '/api/cases/{caseId}/costs', 'Add a cost line'],
      ['PATCH', '/api/costs/{id}', 'Update a cost line'],
      ['DELETE', '/api/costs/{id}', 'Remove a cost line'],
    ],
  },
  {
    name: 'Config',
    authz: 'SuperAdmin (governed keys publish-gated)',
    rows: [
      ['GET', '/api/config', 'All admin config'],
      ['GET', '/api/config/verify-audit', 'Verify the config audit hash-chain'],
      ['PUT', '/api/config/{key}', 'Save one config key'],
      ['PUT', '/api/config', 'Atomic multi-key publish'],
    ],
  },
  {
    name: 'Notifications',
    authz: 'canComment',
    rows: [
      ['POST', '/api/notifications', 'Trigger a notification send'],
      ['POST', '/api/notifications/rules/preview', 'Preview rule recipients'],
    ],
  },
  {
    name: 'PIN (session lock)',
    authz: 'Self / SuperAdmin',
    rows: [
      ['GET', '/api/pin/status', 'PIN status (self, or ?all=1 for SuperAdmin)'],
      ['POST', '/api/pin/set', 'Set / change PIN'],
      ['POST', '/api/pin/verify', 'Unlock with PIN'],
      ['POST', '/api/pin/forgot-request', 'Request a PIN reset'],
      ['DELETE', '/api/pin/{email}', 'Reset a user PIN (SuperAdmin)'],
    ],
  },
  {
    name: 'Access & directory',
    authz: 'SuperAdmin',
    rows: [
      ['GET', '/api/access', 'App-role assignments + assignable roles'],
      ['GET', '/api/access/logs', 'Sign-in / PIN activity log'],
      ['GET', '/api/reference/clinics', 'Clinic directory (TherapieMaps)'],
      ['GET', '/api/reference/staff', 'Department staff directory'],
      ['POST', '/api/access', 'Assign a role to a user'],
      ['POST', '/api/access/approles', 'Create an Entra app role (custom)'],
      ['PATCH', '/api/access', 'Change a user’s role'],
      ['DELETE', '/api/access/{assignmentId}', 'Revoke access'],
    ],
  },
];

function MethodChip({ method }) {
  return (
    <span style={{
      display: 'inline-block', minWidth: 52, textAlign: 'center',
      fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
      padding: '2px 6px', borderRadius: 5, flexShrink: 0,
      color: '#fff', background: METHOD_COLOR[method] || 'var(--text-3)',
    }}>{method}</span>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '4px 0' }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', minWidth: 96, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: 'var(--text)' }}>{children}</span>
    </div>
  );
}

const mono = { fontFamily: 'monospace', fontSize: 12 };

export default function DeveloperDocs() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div className="admin-section-title">Developer</div>
        <div className="admin-section-desc">
          Reference for the AE Tracker HTTP API. Every route is served by the Azure
          Functions app behind <code style={mono}>/api</code>. Read-only — this tab documents the
          surface; it does not issue credentials (yet).
        </div>
      </div>

      {/* Transport + auth */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', background: 'var(--surface)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Base URL &amp; authentication</div>
        <Field label="Base URL"><code style={mono}>https://aetrack.therapietech.com/api</code> — the SPA calls relative <code style={mono}>/api/*</code>; the Static Web App proxies to the linked Function App (same origin).</Field>
        <Field label="Auth"><b>Microsoft Entra ID</b> JWT — <code style={mono}>Authorization: Bearer &lt;token&gt;</code>. Validated server-side (RS256 · audience · tenant).</Field>
        <Field label="Scope"><code style={mono}>{API_SCOPE}</code></Field>
        <Field label="Authorization">Role → capability matrix + geographic scope (clinic / region). Endpoints gate on <em>capabilities</em>, never role strings.</Field>
      </div>

      {/* Error conventions */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', background: 'var(--surface)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Error conventions</div>
        <div style={{ fontSize: 12.5, color: 'var(--text)', display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
          <span><code style={mono}>401</code> missing / invalid token</span>
          <span><code style={mono}>403</code> authenticated but not permitted</span>
          <span><code style={mono}>400</code> validation error</span>
          <span><code style={mono}>404</code> not found</span>
          <span><code style={mono}>409</code> publish-gate / conflict</span>
          <span><code style={mono}>500</code> server error</span>
        </div>
      </div>

      {/* Endpoint catalogue */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Endpoints</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {ENDPOINT_GROUPS.map(group => (
            <div key={group.name} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                padding: '8px 14px', background: 'var(--surface-2, var(--surface))', borderBottom: '1px solid var(--border)',
              }}>
                <span style={{ fontSize: 12.5, fontWeight: 700 }}>{group.name}</span>
                <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{group.authz}</span>
              </div>
              <div>
                {group.rows.map(([method, path, desc]) => (
                  <div key={method + path} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '6px 14px', borderTop: '1px solid var(--border)',
                  }}>
                    <MethodChip method={method} />
                    <code style={{ ...mono, flex: '0 1 auto', color: 'var(--text)', wordBreak: 'break-all' }}>{path}</code>
                    <span style={{ fontSize: 11.5, color: 'var(--text-3)', marginLeft: 'auto', textAlign: 'right' }}>{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API keys — coming soon */}
      <div style={{ border: '1px dashed var(--border)', borderRadius: 10, padding: '12px 16px', background: 'var(--surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Programmatic API keys</span>
          <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>Coming soon</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
          Machine-to-machine access (e.g. read-only reporting for Power BI / Looker) will use
          SuperAdmin-issued keys — hashed at rest, scoped to a capability set + geography, with
          expiry, revocation, and per-key audit. Until then, all access uses interactive Entra
          sign-in. Design: <code style={mono}>api_access_logic.md</code>.
        </div>
      </div>
    </div>
  );
}
